import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import usePrevious from '../../toolkit/useHooks/usePrevious';
import { AWS_BUCKET_URL } from '../../toolkit/constants';
import { useDispatch, useSelector } from 'react-redux';
import { addBetToBetSlip, removeBetFromBetSlip, addBetBuilderSelection, removeBetBuilderSelection, setBetBuilderModalShow, calculateBetBuilderOdds } from 'stores/betSlip';
import { showToaster } from 'mixin/general';
import { useTranslation } from 'react-i18next';
import useOddTransformer from '../../toolkit/useHooks/useOddsTransformer.js';
import { checkIfLiveEvent } from '../../toolkit/utils';
import { isOutcomeConflicting } from '../../toolkit/betBuilder';

function OddButton(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations', { useSuspense: false });
  const betSlip = useSelector((state) => state.betSlip);
  const betBuilder = useSelector((state) => state.betSlip.betBuilder);
  const { providerId, marketStringId, outcomeId } = props;
  const match = useSelector((state) => state.match.events[providerId]);
  const betBuilderOfferedMarkets = useSelector(
    (state) => state.match.betBuilderAvailableSelections?.[providerId]?.data?.markets
  );
  const [selected, setSelected] = useState(false);
  const [betBuilderSelected, setBetBuilderSelected] = useState(false);
  const [oddClass, setOddClass] = useState('');
  // Market/outcome are derived during render (not stored via an effect) so the
  // very first paint already has the odds. Deriving them in a useEffect made the
  // first rendered frame use an empty outcome, which printed NaN until some
  // other interaction re-rendered the button.
  const { market, outcome, renderNull } = useMemo(() => {
    const foundMarket = match?.markets?.find((m) => m.stringId === marketStringId);
    const foundOutcome = foundMarket?.outcomes?.find((o) => o.id === outcomeId);
    return {
      market: foundMarket ?? {},
      outcome: foundOutcome,
      renderNull: !foundMarket || !foundOutcome
    };
  }, [match, marketStringId, outcomeId]);
  const prevOdds = usePrevious(outcome?.odds);
  const updateTimer = useRef(null);
  const hasOdds = typeof outcome?.odds === 'number' && outcome.odds !== 0;
  const oddsTransformer = useOddTransformer();
  const oddBetBox = props.betBox == undefined ||  props.betBox == '' ? 'betOddsBox' : props.betBox;

  // In bet builder mode MyOdds tells which further outcomes would make the selection impossible
  const isBetBuilderConflict =
    betBuilder.isEnabled &&
    !betBuilderSelected &&
    betBuilder.selections[0]?.providerId === providerId &&
    isOutcomeConflicting(betBuilder.conflicts, market.id, marketStringId, outcomeId, betBuilderOfferedMarkets);

  const actionBetSlipOdd = useCallback((e) => {
    e.preventDefault();

    if (betBuilder.isEnabled) {
      // Bet Builder mode
      if (isBetBuilderConflict) {
        showToaster(t('betBuilder.conflictingSelection'), 'error');
        return;
      }
      if (!betBuilderSelected) {
        // Check if there's already a normal bet for this event
        const hasNormalBet = betSlip.bets.some(
          (b) => b.providerId === providerId && !b.isBetBuilder
        );
        if (hasNormalBet) {
          showToaster('If custom bet is added for sport event, you cannot add normal market', 'error');
          return;
        }
        dispatch(
          addBetBuilderSelection({
            providerId: providerId,
            marketStringId: marketStringId,
            marketId: market.id,
            outcomeId: outcomeId,
            outcomeName: outcome?.name,
            odds: outcome?.odds,
            match: match
          })
        );
        // Show modal after adding selection
        dispatch(setBetBuilderModalShow(true));
      } else {
        dispatch(
          removeBetBuilderSelection({
            providerId: providerId,
            marketStringId: marketStringId
          })
        );
      }
    } else {
      // Normal betslip mode
      if (!selected) {
        // Check if there's already a bet builder bet for this event
        const hasBetBuilderBet = betSlip.bets.some(
          (b) => b.providerId === providerId && b.isBetBuilder === true
        );
        if (hasBetBuilderBet) {
          showToaster('If custom bet is added for sport event, you cannot add normal market', 'error');
          return;
        }
        dispatch(
          addBetToBetSlip({
            providerId: providerId,
            marketStringId: marketStringId,
            marketId: market.id,
            outcomeId: outcomeId,
            outcomeName: outcome?.name,
            odds: outcome?.odds,
            match: match
          })
        );
      } else {
        dispatch(
          removeBetFromBetSlip({
            providerId: providerId,
            marketStringId: marketStringId,
            outcomeId: outcomeId
          })
        );
      }
    }
  });

  useEffect(() => {
    // Exclude bet builder bets from showing as selected
    const selectionStatus = betSlip.bets.some(
      (b) =>
        b.providerId === providerId &&
        b.marketStringId === marketStringId &&
        b.outcomeId === outcomeId &&
        !b.isBetBuilder // Don't show bet builder bets as selected
    );
    if (selected !== selectionStatus) {
      setSelected(selectionStatus);
    }
  }, [betSlip]);

  useEffect(() => {
    const betBuilderSelectionStatus = betBuilder.selections.some(
      (s) =>
        s.providerId === providerId &&
        s.marketStringId === marketStringId &&
        s.outcomeId === outcomeId
    );
    if (betBuilderSelected !== betBuilderSelectionStatus) {
      setBetBuilderSelected(betBuilderSelectionStatus);
    }
  }, [betBuilder]);

  //   const usePreviousValue = (value) => {
  //     const ref = useRef();
  //     useEffect(() => {
  //       ref.current = value;
  //     });
  //     return ref.current;
  //   };

  function setUpdate(oddClass) {
    updateTimer.current = setTimeout(() => {
      setOddClass(oddClass);
      updateTimer.current = null;
    }, 5000);
  }

  useEffect(() => {
    const newOdd = outcome?.odds;
    if (prevOdds && newOdd !== prevOdds) {
      setOddClass(` odd-${newOdd > prevOdds ? 'increased' : 'decreased'}`);
      setUpdate('');
    }
  }, [outcome?.odds, prevOdds]);

  useEffect(() => {
    return () => {
      if (updateTimer.current) {
        clearTimeout(updateTimer.current);
        updateTimer.current = null;
      }
    };
  }, []);

  if (renderNull || outcome?.odds == 1.0)
    return (
     <div className={`justify-content-center w-100 px-1 text-center text-muted light-black-svg 
      ${props.betBox == undefined || props.betBox == '' ? 'betOddsBox' : props.betBox}
      `}>
        <img width="20" height="16" src={`${AWS_BUCKET_URL}/Icons/lockicon.svg`} />
      </div>
    );
  const getButtonClass = () => {
    // Show as selected if either in normal betslip or bet builder
    if (betBuilder.isEnabled) {
      if (isBetBuilderConflict) return `${oddBetBox} betOddsBox-conflict`;
      return betBuilderSelected ? 'betOddsBox-selected' : oddBetBox;
    } else {
      return selected ? 'betOddsBox-selected' : oddBetBox;
    }
  };

  return (
    <div
      key={props.outcomeId}
      className={getButtonClass() + oddClass}
      onClick={actionBetSlipOdd}>
      {hasOdds
        ? oddsTransformer.transformWithDisplayFormatted(
            checkIfLiveEvent(match),
            match.isSeason,
            market.id,
            new Date(match.gameDate),
            outcome.odds,
            market.stringId
          )
        : '-'}
      {selected}
    </div>
  );
}
export default OddButton;
